import type { UIMessage } from '@ai-sdk/react'
import {
  type ChatClient,
  type ChatClientOptions,
  ChatHttpError,
  type ChatSnapshot,
  type ConversationContext,
  createBrowserStorage,
  createChatClient,
  createInitialChatState,
  identityScope,
  type MessageSendDisposition,
  restoreSession,
  type UploadedFile,
  type UploadInput,
} from '@plivo/chat-client'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import type { FileUIPart } from 'ai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getLocalAuth } from 'v2/utils'
import { buildBuddyFlowCustomData } from '../buddy-flow-context'
import type { BuddyStatus, UseBuddyChatOptions } from '../use-buddy-chat'
import { type BuddyFlowConfig, getBuddyFlowConfig } from './flow-config'
import { forgetContactId, mintContactId, readContactId } from './flow-contact'
import {
  type OverlayMessage,
  progressToToolParts,
  summarizeTurn,
  type ToolPart,
  toBuddyMessages,
} from './flow-messages'

const EMPTY_SNAPSHOT = createInitialChatState()
const EMPTY_IDS: ReadonlySet<string> = new Set()
// A resumed conversation can sit in awaiting_ai while a pending turn reconciles.
const ACTIVE_WAIT_MS = 20_000

// Tests replace these on the object and restore them, instead of mocking the module.
export const buddyFlowClientFactory = {
  create: (options: ChatClientOptions): ChatClient => createChatClient(options),
  hasStoredSession: async (appId: string, contactId: string): Promise<boolean> => {
    try {
      const scope = identityScope(appId.trim().toLowerCase(), contactId, 'anonymous')
      return (await restoreSession(createBrowserStorage(), scope)) !== null
    } catch {
      return false
    }
  },
}

const currentOwner = (): string => getLocalAuth()?.aom_uuid ?? ''

// The panel recognises a rate limit by this message (is429Error).
const RATE_LIMIT_MESSAGE = '429 Rate limit exceeded'

const rateLimitError = (): Error => new Error(RATE_LIMIT_MESSAGE)

const sendRejection = (disposition: MessageSendDisposition): Error =>
  disposition === 'rejected_rate_limited'
    ? rateLimitError()
    : new Error(`Buddy message was not delivered (${disposition})`)

const toBuddyError = (error: unknown): Error => {
  // A 429 on the conversation's first request is thrown by connect(), not returned by send().
  if (error instanceof ChatHttpError && error.status === 429) return rateLimitError()
  return error instanceof Error ? error : new Error('Buddy request failed')
}

const isEnded = (status: ChatSnapshot['status']): boolean =>
  status === 'closed' || status === 'failed' || status === 'destroyed'

const waitForActive = (client: ChatClient): Promise<void> =>
  new Promise((resolve, reject) => {
    let settled = false
    let unsubscribe = () => {}
    const finish = (error?: Error) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubscribe()
      if (error) reject(error)
      else resolve()
    }
    const check = (snapshot: ChatSnapshot) => {
      if (snapshot.status === 'active') finish()
      else if (isEnded(snapshot.status)) finish(new Error('Buddy conversation ended'))
    }
    const timer = setTimeout(
      () => finish(new Error('Buddy conversation is not ready')),
      ACTIVE_WAIT_MS,
    )
    unsubscribe = client.subscribe(check)
    check(client.getSnapshot())
  })

const MIME_EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
}

// The panel holds attachments as data URLs; the SDK uploads raw bytes and rejects a name
// whose extension does not match the type.
export const toUploadInput = (file: FileUIPart): UploadInput => {
  const base64 = file.url.slice(file.url.indexOf(',') + 1)
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const extension = MIME_EXTENSIONS[file.mediaType] ?? 'bin'
  const hasExtension = file.filename?.toLowerCase().endsWith(`.${extension}`)
  const name = file.filename && hasExtension ? file.filename : `attachment.${extension}`
  return { name, type: file.mediaType, bytes }
}

// customData rejects undefined values, and the flow reads these keys by exact name.
export const buildConversationContext = (queryClient: QueryClient): ConversationContext => {
  const customData = Object.fromEntries(
    Object.entries(buildBuddyFlowCustomData(queryClient)).filter(([, value]) => value != null),
  )
  const [firstName, ...rest] = (getLocalAuth()?.name ?? '').trim().split(/\s+/)
  const authId = typeof customData.auth_id === 'string' ? customData.auth_id : ''
  const email = typeof customData.email === 'string' ? customData.email : ''
  return {
    customData,
    ...(email ? { email } : {}),
    ...(firstName ? { firstName } : {}),
    ...(rest.length ? { lastName: rest.join(' ') } : {}),
    ...(authId ? { externalIdentifier: authId } : {}),
  }
}

const localUserMessage = (id: string, text: string, files: FileUIPart[] = []): UIMessage => ({
  id,
  role: 'user',
  parts: [...(text ? [{ type: 'text' as const, text }] : []), ...files],
})

// Places messages the panel adds itself (the /ticket confirmation) after the message they
// followed, and records the ones it removes, since the SDK owns the real transcript.
const diffPanelEdit = (prev: UIMessage[], next: UIMessage[]) => {
  const prevIds = new Set(prev.map((message) => message.id))
  const nextIds = new Set(next.map((message) => message.id))
  const added: OverlayMessage[] = []
  next.forEach((message, index) => {
    if (!prevIds.has(message.id)) {
      added.push({ afterId: index > 0 ? next[index - 1].id : null, message })
    }
  })
  const removed = prev.filter((message) => !nextIds.has(message.id)).map((message) => message.id)
  return { added, removed }
}

export const useBuddyFlowChat = (opts: UseBuddyChatOptions) => {
  const queryClient = useQueryClient()
  const config = useMemo<BuddyFlowConfig | null>(getBuddyFlowConfig, [])
  const optsRef = useRef(opts)
  optsRef.current = opts

  const clientRef = useRef<ChatClient | null>(null)
  const unsubscribeRef = useRef<(() => void) | null>(null)
  // Bumped by reset and stop so results of an abandoned turn are ignored.
  const generationRef = useRef(0)
  const toolsByTurnRef = useRef(new Map<string, ToolPart[]>())
  const inFlightTurnRef = useRef<string | undefined>(undefined)
  const messagesRef = useRef<UIMessage[]>([])

  const [snapshot, setSnapshot] = useState<ChatSnapshot>(EMPTY_SNAPSHOT)
  const [inFlightTurnId, setInFlightTurnId] = useState<string | undefined>(undefined)
  const [pendingUser, setPendingUser] = useState<UIMessage | undefined>(undefined)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [overlay, setOverlay] = useState<OverlayMessage[]>([])
  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(EMPTY_IDS)

  const attach = useCallback((client: ChatClient) => {
    unsubscribeRef.current?.()
    clientRef.current = client
    const onSnapshot = (next: ChatSnapshot) => {
      // Captured on every update: the SDK empties progress as soon as the turn completes.
      const turnId = inFlightTurnRef.current
      if (turnId && next.progress.length) {
        toolsByTurnRef.current.set(turnId, progressToToolParts(next.progress))
      }
      setSnapshot(next)
    }
    unsubscribeRef.current = client.subscribe(onSnapshot)
    onSnapshot(client.getSnapshot())
  }, [])

  const release = useCallback((client: ChatClient | null, clearSession: boolean) => {
    if (!client) return
    if (clientRef.current === client) {
      unsubscribeRef.current?.()
      unsubscribeRef.current = null
      clientRef.current = null
    }
    const dispose = clearSession ? client.clearSession() : client.destroy()
    void dispose.catch(() => client.destroy().catch(() => undefined))
  }, [])

  const createClient = useCallback(
    (flow: BuddyFlowConfig, contactId: string): ChatClient =>
      buddyFlowClientFactory.create({
        appId: flow.appId,
        appKey: flow.appKey,
        region: flow.region,
        ...(flow.apiOrigin ? { apiOrigin: flow.apiOrigin } : {}),
        ...(flow.localDevelopment ? { localDevelopment: flow.localDevelopment } : {}),
        identity: { mode: 'anonymous', visitorId: contactId },
        conversationContext: buildConversationContext(queryClient),
      }),
    [queryClient],
  )

  const clearLocalThread = useCallback(() => {
    toolsByTurnRef.current = new Map()
    setOverlay([])
    setHiddenIds(EMPTY_IDS)
  }, [])

  // Resume the member's stored conversation, but never bootstrap one just for opening Buddy.
  useEffect(() => {
    if (!config) return
    const contactId = readContactId(currentOwner())
    if (!contactId) return
    let cancelled = false
    void (async () => {
      if (!(await buddyFlowClientFactory.hasStoredSession(config.appId, contactId))) return
      if (cancelled || clientRef.current) return
      const client = createClient(config, contactId)
      attach(client)
      try {
        await client.connect()
      } catch {
        // Unresumable: the next message starts a fresh conversation instead.
        if (clientRef.current === client) release(client, true)
        forgetContactId(currentOwner())
      }
    })()
    return () => {
      cancelled = true
    }
  }, [config, createClient, attach, release])

  useEffect(
    () => () => {
      generationRef.current++
      release(clientRef.current, false)
    },
    [release],
  )

  // Every new server conversation gets a new contact id, so an old one still open on the
  // server is never bootstrapped again.
  const ensureActiveClient = useCallback(
    async (flow: BuddyFlowConfig): Promise<ChatClient> => {
      let client = clientRef.current
      if (client && isEnded(client.getSnapshot().status)) {
        release(client, true)
        forgetContactId(currentOwner())
        clearLocalThread()
        client = null
      }
      if (!client) {
        client = createClient(flow, mintContactId(currentOwner()))
        attach(client)
      }
      if (client.getSnapshot().status === 'idle') {
        try {
          await client.connect()
        } catch (connectError) {
          if (clientRef.current === client) release(client, false)
          forgetContactId(currentOwner())
          throw connectError
        }
      }
      await waitForActive(client)
      return client
    },
    [attach, clearLocalThread, createClient, release],
  )

  const sendMessage = useCallback(
    async ({ text, files }: { text: string; files?: FileUIPart[] }) => {
      if (!config) return
      const generation = ++generationRef.current
      const isCurrent = () => generation === generationRef.current
      const turnId = crypto.randomUUID()
      const shownMessage = localUserMessage(turnId, text, files)
      inFlightTurnRef.current = turnId
      setInFlightTurnId(turnId)
      setPendingUser(shownMessage)
      setError(null)
      setPending(true)
      try {
        const client = await ensureActiveClient(config)
        if (!isCurrent()) return
        const attachments: UploadedFile[] = []
        for (const file of files ?? []) {
          attachments.push(await client.upload(toUploadInput(file)))
          if (!isCurrent()) return
        }
        const receipt = await client.send({
          kind: 'text',
          text,
          clientMessageId: turnId,
          ...(attachments.length ? { attachments } : {}),
        })
        if (!isCurrent()) return
        if (receipt.disposition !== 'accepted') throw sendRejection(receipt.disposition)
        const settled = client.getSnapshot()
        const turnMessages = toBuddyMessages({
          snapshot: settled,
          toolsByTurn: toolsByTurnRef.current,
          overlay: [],
          hiddenIds: EMPTY_IDS,
        })
        optsRef.current.onFinish?.({
          ...summarizeTurn(turnMessages, turnId),
          sessionId: settled.session?.conversationId,
        })
      } catch (sendError) {
        if (!isCurrent()) return
        const buddyError = toBuddyError(sendError)
        // A message that never reached the SDK stays in the thread, as it does today; a
        // rate-limited one is dropped and its text goes back to the composer instead.
        const reachedSdk = clientRef.current
          ?.getSnapshot()
          .messages.some((message) => message.id === turnId)
        if (!reachedSdk && buddyError.message !== RATE_LIMIT_MESSAGE) {
          const previous = messagesRef.current.filter((message) => message.id !== turnId).at(-1)
          setOverlay((current) => [
            ...current,
            { afterId: previous?.id ?? null, message: shownMessage },
          ])
        }
        setError(buddyError)
        optsRef.current.onError?.(buddyError)
      } finally {
        if (isCurrent()) {
          inFlightTurnRef.current = undefined
          setInFlightTurnId(undefined)
          setPendingUser(undefined)
          setPending(false)
        }
      }
    },
    [config, ensureActiveClient],
  )

  const stop = useCallback(() => {
    generationRef.current++
    inFlightTurnRef.current = undefined
    setInFlightTurnId(undefined)
    setPendingUser(undefined)
    setPending(false)
  }, [])

  const resetConversation = useCallback(() => {
    stop()
    release(clientRef.current, true)
    forgetContactId(currentOwner())
    clearLocalThread()
    setSnapshot(EMPTY_SNAPSHOT)
    setError(null)
  }, [clearLocalThread, release, stop])

  const clearError = useCallback(() => setError(null), [])

  const messages = useMemo(
    () =>
      toBuddyMessages({
        snapshot,
        toolsByTurn: toolsByTurnRef.current,
        inFlightTurnId,
        pendingUser,
        overlay,
        hiddenIds,
      }),
    [snapshot, inFlightTurnId, pendingUser, overlay, hiddenIds],
  )
  messagesRef.current = messages

  const setMessages = useCallback((updater: UIMessage[] | ((prev: UIMessage[]) => UIMessage[])) => {
    const prev = messagesRef.current
    const next = typeof updater === 'function' ? updater(prev) : updater
    const { added, removed } = diffPanelEdit(prev, next)
    if (added.length) setOverlay((current) => [...current, ...added])
    if (removed.length) setHiddenIds((current) => new Set([...current, ...removed]))
  }, [])

  const busy = pending || snapshot.status === 'awaiting_ai'
  const status: BuddyStatus = busy
    ? snapshot.aiActivity === 'streaming'
      ? 'streaming'
      : 'submitted'
    : error
      ? 'error'
      : 'ready'

  return {
    messages,
    sendMessage,
    setMessages,
    resetConversation,
    status,
    error,
    clearError,
    stop,
  }
}
